import { Link } from "react-router-dom";
import { CardBody, CardContainer, CardItem } from "../../ui/cardthreedimension";
import SanityImage from "../SanityImage/SanityImage";
import { IMAGE_SIZES } from "../ResponsiveImage/imageSizes";
import type { SanityImageData } from "@/types/sanity";

interface CardWithImageProps {
  cardTitle: string;
  cardContent: string;
  /** Bundled/static image URL. Ignored when `image` is provided. */
  imageUrl?: string;
  /** Sanity image; rendered responsively through the image pipeline. */
  image?: SanityImageData | null;
  linkUrl: string;
  disableLink?: boolean;
}

const IMAGE_CLASS = "w-full h-[140px] md:h-[240px] object-cover rounded-xl group-hover/card:shadow-xl";

const CardWithImage: React.FC<CardWithImageProps> = ({
  cardTitle,
  cardContent,
  imageUrl,
  image,
  linkUrl = "/",
  disableLink = false,
}) => {
  const content = (
    <CardContainer className="inter-var w-full max-w-[500px] mx-auto">
      <CardBody className="bg-gray-100 relative group dark:hover:shadow-2xl dark:hover:shadow-emerald-500/[0.1] dark:bg-custom-darkblue dark:border-white/[0.2] border-black/[0.1] rounded-xl p-6 border hover:cursor-pointer hover:bg-custom-secolor flex flex-col h-full">
        <div className="flex flex-col justify-start flex-grow">
          {/* Title */}
          <CardItem
            translateZ="5"
            className="text-left text-[clamp(1rem,2vw,1.5rem)] font-bold text-neutral-600 text-custom-darkblue group-hover:text-white line-clamp-2 min-h-[3.5rem] opacity-90"
          >
            {cardTitle}
          </CardItem>

          {/* Content Paragraph */}
          <CardItem
            as="p"
            translateZ="5"
            className="text-neutral-500 dark:text-neutral-300 text-left leading-relaxed line-clamp-2 md:line-clamp-3 md:text-base group-hover:text-white group-hover:opacity-90 opacity-70"
          >
            {cardContent}
          </CardItem>
        </div>

        {/* Image */}
        <CardItem translateZ="5" className="w-full mt-4">
          {image ? (
            <SanityImage
              image={image}
              alt={cardTitle}
              sizes={IMAGE_SIZES.card}
              className={IMAGE_CLASS}
            />
          ) : (
            <img
              src={imageUrl}
              alt={cardTitle}
              loading="lazy"
              decoding="async"
              className={IMAGE_CLASS}
            />
          )}
        </CardItem>
      </CardBody>
    </CardContainer>
  );

  return disableLink ? content : <Link to={linkUrl}>{content}</Link>;
};

export default CardWithImage;
